import React, { useContext } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import media from 'styled-media-query';
import { breakpointsObject as bp } from '@firsttable/web-theme';
import { countFormat, roundToClosest } from '@firsttable/functions';
import Box from '@firsttable/web-components/atoms/Box';
import Flex from '@firsttable/web-components/atoms/Flex';
import Col from '@firsttable/web-components/atoms/Col';
import Row from '@firsttable/web-components/atoms/Row';
import Container from '@firsttable/web-components/atoms/Container';
import Link from '@firsttable/web-components/atoms/Link';
import Text from '@firsttable/web-components/atoms/Text';
import Title from '@firsttable/web-components/atoms/Title/Title';
import BodyTitle from '@firsttable/web-components/atoms/BodyTitle';
import Image from 'next/image';
import { Divider } from '@firsttable/web-components/atoms';
import IconFacebook from '@firsttable/web-components/atoms/Icon/IconFacebook';
import IconInstagram from '@firsttable/web-components/atoms/Icon/IconInstagram';
import Button from '@components/analytics/components/Button';
import { TrackProvider } from '@components/analytics/context/AnalyticsContext';
import withMouseDown from '@/components/analytics/hocs/withMouseDown';
import img01 from './Group-3.svg?url';
import SilverstripeContext from '../../context/Silverstripe';
import NavLinkComponent from '../../GatsbyLinkConvertor';
import NavButtonComponent from '../../GatsbyButtonConvertor';
import appStoreBadge from './images/app-store-badge.png';
import googlePlayBadge from './images/google-play-badge.png';
import PrivacyAndTermsLinks from './PrivacyAndTermsLinks';
import { isGiftVoucherPath, isGiftVouchersEnabledForSite } from '../../../lib/giftVouchers';

const maxFooterColumns = 5;

const StyledFooter = styled(Box)`
  flex-shrink: 0;
  background: ${themeGet('colors.footer.bg')} url('${img01}') no-repeat calc(50% - 660px) 66px;
  content-visibility: auto;
  contain-intrinsic-size: 850px;
  ${media.lessThan(bp.s)`
    contain-intrinsic-size: 1150px;
  `}
`;
const Socials = styled(Flex)`
  .icon {
    circle {
      fill: ${themeGet('colors.footer.btn')};
      transition: 0.1s ease;
    }

    &:hover {
      circle {
        fill: ${themeGet('colors.footer.btnHover')};
      }
    }
  }
`;

const ColumnTitle = styled(BodyTitle)`
  color: ${themeGet('colors.white')};
  font-size: ${themeGet('fontSizes.m')}px;
  font-weight: normal;
  margin: 0 0 5px;
  ${media.lessThan(bp.s)`
    //text-align: center;
  `};
`;

const ColumnItem = styled(Box)`
  font-size: ${themeGet('fontSizes.s')}px;
  margin: 0 0 5px;
  ${media.lessThan(bp.s)`
    //text-align: center;
  `};
`;
const LinkWithTracking = withMouseDown(Link);

const Footer = () => {
  const {
    global: { navigation, siteConfig },
  } = useContext(SilverstripeContext);

  // don't show menu if no global state. likely not been populated
  if (!siteConfig || !navigation) {
    return null;
  }

  const footerColumns = navigation
    .filter((node) => node.children.edges.length)
    .slice(0, maxFooterColumns);

  return (
    <StyledFooter pb="70px" pt={['50px', '160px']}>
      <TrackProvider section="footer">
        <Container>
          <Row mb={['40px', '120px']} textAlign="center">
            <Col width={[1 / 3]} display={['none', 'block']}>
              <TrackProvider component_name="diners">
                <Title color="white" size="h5" as="h2" m="0 0 14px">
                  Diners
                </Title>
                <Text maxWidth="258px" m="auto" fontSize="l" color="grayscale.600" mb="22px">
                  Join more than{' '}
                  {countFormat(roundToClosest(Math.max(500000, siteConfig.dinerCount), 100000))}{' '}
                  diners who already ❤ First Table.
                </Text>
                <TrackProvider button_name="learn more" button_type="navigation">
                  <Button
                    NavComponent={NavButtonComponent}
                    to="/frequently-asked-questions/"
                    size="s"
                    kind="white-transparent">
                    Learn more
                  </Button>
                </TrackProvider>
              </TrackProvider>
            </Col>
            <Col
              width={[1 / 3]}
              display={['none', 'block']}
              line
              lineColor="rgba(255, 255, 255, 0.07)">
              <TrackProvider component_name="restaurateurs">
                <Title color="white" size="h5" as="h2" m="0 0 14px">
                  Restaurateurs
                </Title>
                <Text maxWidth="271px" m="auto" fontSize="l" color="grayscale.600" mb="22px">
                  Join more than {countFormat(roundToClosest(siteConfig.restaurantCount, 100))}{' '}
                  restaurants who already ❤ First Table.
                </Text>
                <TrackProvider button_name="learn more" button_type="navigation">
                  <Button
                    NavComponent={NavButtonComponent}
                    to="/restaurants/"
                    kind="white-transparent"
                    size="s">
                    Learn more
                  </Button>
                </TrackProvider>
              </TrackProvider>
            </Col>
            <Col width={[1, 1 / 3]} line lineColor="rgba(255, 255, 255, 0.07)" hideLineOnMobile>
              <TrackProvider component_name="follow us">
                <Title color="white" size="h5" as="h2" m="0 0 14px">
                  Follow us
                </Title>
                <Text
                  maxWidth={[null, '278px']}
                  m="auto"
                  fontSize={['s', 'l']}
                  color="grayscale.600"
                  mb="22px">
                  Share the love by following and tagging us on social media.
                </Text>
                <Socials display="inline-flex">
                  <Box m="0 6px">
                    <TrackProvider
                      button_name="facebook"
                      button_copy="facebook icon"
                      button_type="navigation">
                      <LinkWithTracking href={siteConfig.facebookURL} aria-label="facebook">
                        <IconFacebook className="icon" />
                      </LinkWithTracking>
                    </TrackProvider>
                  </Box>
                  <Box m="0 6px">
                    <TrackProvider
                      button_name="instagram"
                      button_copy="facebook icon"
                      button_type="navigation">
                      <LinkWithTracking href={siteConfig.instagramURL} aria-label="instagram">
                        <IconInstagram className="icon" />
                      </LinkWithTracking>
                    </TrackProvider>
                  </Box>
                </Socials>
              </TrackProvider>
            </Col>
          </Row>
          <Container maxWidth={1070} mx="auto" pl={0} pr={0}>
            <Row justifyContent="space-between">
              {footerColumns.map((node, index) => (
                <Box key={node.id} width={[1, 'auto']}>
                  <ColumnTitle as="h5">
                    {/* <Link NavComponent={NavLinkComponent} to={node.slug}> */}
                    {node.menuTitle}
                    {/* </Link> */}
                  </ColumnTitle>
                  {node.children.edges
                    .filter(
                      ({ node: childNode }) =>
                        isGiftVouchersEnabledForSite() || !isGiftVoucherPath(childNode.slug)
                    )
                    .map(({ node: childNode }) => (
                      <ColumnItem key={childNode.id}>
                        <TrackProvider link_name={childNode.slug}>
                          <Link
                            NavComponent={NavLinkComponent}
                            to={childNode.slug}
                            color="grayscale.600">
                            {childNode.menuTitle}
                          </Link>
                        </TrackProvider>
                      </ColumnItem>
                    ))}
                  {index !== footerColumns.length - 1 && (
                    <Divider my="16px" display={[null, 'none']} bg="grayscale.600" opacity={0.5} />
                  )}
                </Box>
              ))}
            </Row>
          </Container>
          <Container maxWidth={1070} mx="auto" mt={[20, 0]}>
            <Row my={[0, 50]} flexDirection={['column-reverse', 'row']}>
              <Col
                width={[1, 1 / 2]}
                display="flex"
                alignItems="center"
                gridGap={[10, 50]}
                flexWrap="wrap">
                <Text
                  color="grayscale.600"
                  fontSize="s"
                  m={0}
                  width={['100%', 'auto']}
                  textAlign={['center', 'left']}>
                  © First Table Limited {new Date().getFullYear()}
                </Text>
                <Text fontSize="s" m={0} width={['100%', 'auto']} textAlign={['center', 'left']}>
                  <PrivacyAndTermsLinks className="text-ds-gray-light-400" />
                </Text>
              </Col>
              <Col
                width={[1, 1 / 2]}
                margin={['20px 0', 0]}
                display="flex"
                alignItems="center"
                justifyContent={['center', 'flex-end']}
                gridGap="10px">
                <TrackProvider
                  button_name="google play"
                  button_type="navigation"
                  button_copy="GET IT ON Google Play">
                  <LinkWithTracking href="https://play.google.com/store/apps/details?id=com.firsttable.live&referrer=utm_source%3Dwebsite_footer%26utm_medium%3Dwebsite%26utm_campaign%3Dnone">
                    <Image
                      src={googlePlayBadge}
                      alt="Google Play"
                      width={googlePlayBadge.width / 3.5}
                      height={googlePlayBadge.height / 3.5}
                    />
                  </LinkWithTracking>
                </TrackProvider>{' '}
                <TrackProvider
                  button_name="apple app store"
                  button_type="navigation"
                  button_copy="Download on the App Store">
                  <LinkWithTracking href="https://apps.apple.com/app/apple-store/id1599994101?pt=121303410&ct=website%20footer%20referral&mt=8">
                    <Image
                      src={appStoreBadge}
                      alt="App Store"
                      width={appStoreBadge.width / 3.5}
                      height={appStoreBadge.height / 3.5}
                    />
                  </LinkWithTracking>
                </TrackProvider>
              </Col>
            </Row>
          </Container>
        </Container>
      </TrackProvider>
    </StyledFooter>
  );
};

Footer.propTypes = {};
export default Footer;
